import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useConfirm } from 'material-ui-confirm';
import { translate as t } from 'hooks/useTranslations';
import { AppContext } from 'views/App/context';
import { DraftManualDetailContext } from '../context';
import useDraftPreface from 'views/App/Portal/Policy/DraftManuals/_data_sources/useDraftPreface';
import useManagePreface from 'views/App/Portal/Policy/DraftManuals/_data_sources/useManagePreface';
import { DraftPreface, DraftManual } from 'views/App/Portal/Policy/_types/manuals/draft';
import { ServerErrorResponse } from 'data_sources/constants';
import errorHelper from 'helpers/errorHelper';
import { enqueueSnackbar } from 'notistack';
import useManageManual from 'views/App/Portal/Policy/DraftManuals/_data_sources/useManageManual';
import useAccreditationTags from 'views/App/Portal/AWB/_data_sources/useAccreditationTags';

interface PrefaceEditorContextProps {
  prefaceData: DraftPreface | null;
  addAttachmentsToPreface: (newAttachments: { id: string; embedded: boolean }[]) => void;
  removeAttachmentFromPreface: (attachmentId: string) => void;
  acceptNewPreface: () => Promise<void>;
  rejectNewPreface: () => Promise<void>;
  isAcceptingNew: boolean;
  isRejectingNew: boolean;
  accreditationTags: Record<string, string[]>;
}

export const PrefaceEditorContext = createContext<PrefaceEditorContextProps | undefined>(undefined);

export const PrefaceEditorProvider: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => {
  const queryClient = useQueryClient();
  const { manual } = useContext(DraftManualDetailContext);

  const prefaceQuery = useDraftPreface(manual.id, id);
  const [prefaceData, setPrefaceData] = useState<DraftPreface | null>(null);
  const { update: updatePreface } = useManagePreface();
  const { update: updateManual, getLock: getManualLock, removeLock: removeManualLock } = useManageManual();
  const [isAcceptingNew, setIsAcceptingNew] = useState(false);
  const [isRejectingNew, setIsRejectingNew] = useState(false);
  const [transformedAccreditationTags, setTransformedAccreditationTags] = useState<Record<string, string[]>>({});
  const accreditationTagsQuery = useAccreditationTags(manual.id, id);

  useEffect(() => {
    if (prefaceQuery.data) {
      setPrefaceData(prefaceQuery.data);
    }
  }, [prefaceQuery.data]);

  useEffect(() => {
    if (accreditationTagsQuery.data) {
      const transformed = accreditationTagsQuery.data.reduce(
        (acc, item) => {
          const contentId = item.contentUri.split('/').pop() || '';
          const standardText = `${item.bodyAbbreviation} ${item.bodyVersionAbbreviation} ${item.standardHeading}: ${item.standardTitle}`;

          if (!acc[contentId]) {
            acc[contentId] = [];
          }
          if (!acc[contentId].includes(standardText)) {
            acc[contentId].push(standardText);
          }
          return acc;
        },
        {} as Record<string, string[]>
      );

      setTransformedAccreditationTags(transformed);
    }
  }, [accreditationTagsQuery.data]);

  const addAttachmentsToPreface = useCallback(
    (newAttachments: { id: string; embedded: boolean }[]) => {
      setPrefaceData((prev) => {
        if (!prev) return prev;
        const updated = {
          ...prev,
          attachments: [...(prev.attachments || []), ...newAttachments],
        };
        updatePreface(
          manual.id,
          id,
          updated,
          () => {
            queryClient.invalidateQueries(['draft_preface', manual.id, id]);
          },
          (err: ServerErrorResponse) => {
            console.error('Error updating preface with attachments:', err);
            errorHelper.enqueueErrors(err);
          }
        );
        return updated;
      });
    },
    [manual.id, id, updatePreface, queryClient]
  );

  const removeAttachmentFromPreface = useCallback(
    (attachmentId: string) => {
      setPrefaceData((prev) => {
        if (!prev) return prev;
        const updated = {
          ...prev,
          attachments: (prev.attachments || []).filter((a) => a.id !== attachmentId),
        };
        updatePreface(
          manual.id,
          id,
          updated,
          () => {
            queryClient.invalidateQueries(['draft_preface', manual.id, id]);
          },
          (err: ServerErrorResponse) => {
            console.error('Error removing attachment from preface:', err);
            errorHelper.enqueueErrors(err);
          }
        );
        return updated;
      });
    },
    [manual.id, id, updatePreface, queryClient]
  );

  const acceptNewPreface = useCallback(async () => {
    if (!prefaceData) return;

    setIsAcceptingNew(true);
    try {
      await new Promise<void>((resolve, reject) => {
        getManualLock(
          manual.id,
          (lock) => {
            const updatedManual: DraftManual = { ...manual };
            updatedManual.prefaces = updatedManual.prefaces.map((preface) => {
              if (preface.id === id) {
                return {
                  ...preface,
                  state: 'ENABLED',
                  isNew: false,
                };
              }
              return preface;
            });

            updateManual(
              manual.id,
              updatedManual,
              () => {
                removeManualLock(
                  manual.id,
                  () => {
                    queryClient.invalidateQueries('draft_manuals');
                    queryClient.invalidateQueries(['draft_manual', manual.id]);
                    queryClient.invalidateQueries(['draft_preface', manual.id, id]);
                    setIsAcceptingNew(false);
                    enqueueSnackbar(t('draftManuals.preface.newPreface.accepted'), { variant: 'success' });
                    resolve();
                  },
                  (error: ServerErrorResponse) => {
                    errorHelper.enqueueErrors(error);
                    setIsAcceptingNew(false);
                    reject(error);
                  }
                );
              },
              (error: ServerErrorResponse) => {
                removeManualLock(
                  manual.id,
                  () => {},
                  () => {}
                );
                errorHelper.enqueueErrors(error);
                setIsAcceptingNew(false);
                reject(error);
              }
            );
          },
          (error: ServerErrorResponse) => {
            errorHelper.enqueueErrors(error);
            setIsAcceptingNew(false);
            reject(error);
          }
        );
      });
    } catch (error) {
      console.error('Error accepting new preface:', error);
    }
  }, [prefaceData, manual, id, getManualLock, updateManual, removeManualLock, queryClient, t]);

  const rejectNewPreface = useCallback(async () => {
    if (!prefaceData) return;

    setIsRejectingNew(true);
    try {
      await new Promise<void>((resolve, reject) => {
        getManualLock(
          manual.id,
          (lock) => {
            const updatedManual: DraftManual = { ...manual };

            updatedManual.prefaces = updatedManual.prefaces.map((preface) => {
              if (preface.id === id) {
                return {
                  ...preface,
                  isNew: false,
                };
              }
              return preface;
            });

            updateManual(
              manual.id,
              updatedManual,
              () => {
                removeManualLock(
                  manual.id,
                  () => {
                    queryClient.invalidateQueries('draft_manuals');
                    queryClient.invalidateQueries(['draft_manual', manual.id]);
                    queryClient.invalidateQueries(['draft_preface', manual.id, id]);
                    setIsRejectingNew(false);
                    enqueueSnackbar(t('draftManuals.preface.newPreface.rejected'), { variant: 'info' });
                    resolve();
                  },
                  (error: ServerErrorResponse) => {
                    errorHelper.enqueueErrors(error);
                    setIsRejectingNew(false);
                    reject(error);
                  }
                );
              },
              (error: ServerErrorResponse) => {
                removeManualLock(
                  manual.id,
                  () => {},
                  () => {}
                );
                errorHelper.enqueueErrors(error);
                setIsRejectingNew(false);
                reject(error);
              }
            );
          },
          (error: ServerErrorResponse) => {
            errorHelper.enqueueErrors(error);
            setIsRejectingNew(false);
            reject(error);
          }
        );
      });
    } catch (error) {
      console.error('Error rejecting new preface:', error);
    }
  }, [prefaceData, manual, id, getManualLock, updateManual, removeManualLock, queryClient, t]);

  const contextValue = useMemo(
    () => ({
      prefaceData,
      addAttachmentsToPreface,
      removeAttachmentFromPreface,
      acceptNewPreface,
      rejectNewPreface,
      isAcceptingNew,
      isRejectingNew,
      accreditationTags: transformedAccreditationTags,
    }),
    [
      prefaceData,
      addAttachmentsToPreface,
      removeAttachmentFromPreface,
      acceptNewPreface,
      rejectNewPreface,
      isAcceptingNew,
      isRejectingNew,
      transformedAccreditationTags,
    ]
  );

  return <PrefaceEditorContext.Provider value={contextValue}>{children}</PrefaceEditorContext.Provider>;
};

export const usePrefaceEditorContext = () => {
  const context = useContext(PrefaceEditorContext);
  if (!context) {
    throw new Error('usePrefaceEditorContext must be used within a PrefaceEditorProvider');
  }
  return context;
};
